//import required libraries
import React, { useContext } from "react";
import { useTranslation } from "react-i18next";

//import library components
import { makeStyles } from "@material-ui/core";

//import custom components
import CardChart from "./CardChart";
import CardCreate from "./CardCreate";
import CardGrid from "./CardGrid";

// Import contexts
import { ThemeContext } from "../../contexts/ThemeContext";

//utility functions
function hexToRGBA(hex = "#000000", alpha = 1) {
  const [r, g, b] = hex.match(/\w\w/g).map((x) => parseInt(x, 16));
  return `rgba(${r},${g},${b},${alpha})`;
}

function AppCard(props) {
  const { t } = useTranslation();
  const { state: themeState } = useContext(ThemeContext);
  const themeColors = themeState.themes[themeState.activeTheme];
  const { payload } = props;
  const { tag, gridLayoutData } = payload;
  let Component = <></>;

  //making styles for all variations
  const useStyles = makeStyles(() => ({
    card: {
      height: "150px",
      width: "100%",
      backgroundColor: themeColors["buttonColor"],
      boxSizing: "border-box",
      borderRadius: "8px",
      border: props.payload?.isSelected ? `1px solid ${themeColors["primaryColor"]}` : "1px solid #E4E2DE",
      // boxShadow: "0px 0px 10px rgba(239, 242, 245, 0.4)",
      cursor: "pointer",
      transition: "transform 0.1s linear",

      "&:hover": {
        border: `1px solid ${themeColors["primaryColor"]}`,
        //EXPERIMENTAL
        // "& .card-title-container": {
        //   backgroundColor: themeColors["primaryColor"],
        //   "& .card-title": { color: "#ffffff" },
        // },
      },

      "& *": {
        boxSizing: "border-box",
      },

      "&>.card-image-container": {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        backgroundRepeat: "no-repeat",
        backgroundPosition: "center",
        backgroundSize: "contain",
      },

      "&>.card-image-body-container": {
        height: "calc(100% - 44px)",
        position: "relative",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        overflow: "hidden",

        "&>.card-overlay-container": {
          position: "absolute",
          top: 0,
          left: 0,
          height: "100%",
          width: "100%",
          backgroundColor: props.payload.isSelected ? `${themeColors["primaryColor"]}1A` : "transparent",
          borderRadius: "7px 7px 0 0",

          "& > .card-chartName-container": {
            position: "absolute",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "20px",
            top: "5px",
            right: props.payload.isSelected ? "24px" : "5px",
            padding: "3px 8px",
            color: themeColors["secondaryColor"],
            borderRadius: "999em",
            fontSize: "9px",
            backgroundColor: `${themeColors["primaryColor"]}1A`,
          },
          "& .card-tick-icon-container": {
            position: "absolute",
            top: "8px",
            right: "5px",
            width: "14px",
            height: "14px",
            backgroundColor: "white !important",
            borderRadius: "50%",

            "&>span": {
              fontSize: "14px !important",
              color: themeColors["primaryColor"],
            },
          },
        },
        "& > .last-accessed-option-icon-container": {
          position: "absolute",
          left: "5px",
          top: "6px",
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",

          "& > span": {
            fontSize: "10px",
            marginRight: "3px",
            color: themeColors["secondaryColor"],
          },
        }
      },

      "&>.card-title-container": {
        height: "22px",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        // backgroundColor: "rgba(239, 242, 245, 0.4)",
        backgroundColor: props.payload?.isSelected ? `${themeColors["primaryColor"]}` : "transparent",
        borderTop: props.payload?.isSelected ? `1px solid ${themeColors["primaryColor"]}` : "1px solid #ccc",

        "&>*": {
          color: props.payload?.isSelected ? "white" : themeColors["secondaryColor"],
          fontSize: "11px",
          fontWeight: "500"
        },
        "&>.card-title": {
          overflow: "hidden",
          whiteSpace: "nowrap",
          textOverflow: "ellipsis",
        },

        "&>.more-option-icon-container": {
          // width: "16px",
          // height: "16px",
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",

          "&> span": {
            fontSize: "14px",
          },
        },
      },

      "&>.card-subtitle-container": {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        height: "22px",
        fontSize: "10px",
        color: props.payload?.isSelected ? "white" : themeColors["secondaryColor"],
        backgroundColor: props.payload?.isSelected ? `${themeColors["primaryColor"]}` : "transparent",
        borderRadius: "0px 0px 7px 7px",

        "&>.card-subtitle": {
          overflow: "hidden",
          whiteSpace: "nowrap",
          textOverflow: "ellipsis",
        },

        "&>.last-accessed-option-icon-container": {
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",

          "&> span": {
            fontSize: "10px",
            marginRight: "3px",
          },
        }
      },

      "&.card-create-and-nochart": {
        textAlign: "center",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        // * Different Variation
        border: "1px solid transparent",
        boxShadow: "none",
        backgroundColor: "transparent",
        "&:hover": { border: `1px solid transparent` },
      },

      "&.card-create": {
        backgroundColor: themeColors["primaryColor"],
        color: "#ffffff",
        "&:hover": { border: `1px solid #eff2f5`, transform: "scale(1.05)" },
        "&>.card-title": {
          marginTop: "7px !important",
          fontSize: "14px",
        }, //> not working
      },

      "&.card-nochart": {
        "&:hover": { border: `1px solid #eff2f5` },
        cursor: "auto",
        "&>.card-title": {
          color: themeColors["primaryColor"],
          fontSize: "12px",
          marginTop: "9px !important",
        },
      },
    },
  }));

  const classes = useStyles();

  switch (tag) {
    case "chart":
    case "chartWithIcon":
      Component = (
        <CardChart {...props} classes={classes} gridData={gridLayoutData} />
      );
      break;
    case "gridChart":
      Component = (
        <CardGrid {...props} classes={classes} gridData={gridLayoutData} />
      );
      break;
    case "createNew":
    case "noCharts":
      Component = <CardCreate {...props} classes={classes} />;
      break;
    default:
      Component = (<h1>t("Something Went Wrong!")</h1>);
      break;
  }
  return Component;
}

export default AppCard;
